<script setup lang="ts">
/**
 * CargoPanel — Cargo hold management panel
 * Shows cargo capacity stats and cargo item list.
 * Accepts drag-and-drop from market browser.
 */
import { computed, ref } from 'vue';
import { useFitStore } from '../../stores/fitStore';
import { iconUrl } from '../../composables/useApi';
import type { FitCargo } from '../../../shared/types';
import CargoQtyDialog from '../modals/CargoQtyDialog.vue';

const store = useFitStore();

const fit = computed(() => store.activeFit);
const cargo = computed<FitCargo[]>(() => fit.value?.cargo ?? []);

// Quantity dialog state
const showQtyDialog = ref(false);
const pendingCargo = ref<{ typeID: number; typeName: string } | null>(null);

const capacityUsed = computed(() => {
  return cargo.value.reduce((sum, c) => sum + c.volume * c.quantity, 0);
});
const capacityTotal = computed(() => fit.value?.shipCargoCapacity ?? 0);

function removeCargo(c: FitCargo): void {
  store.removeCargoFromFit(c.id);
}

function handleDrop(e: DragEvent): void {
  e.preventDefault();
  const data = e.dataTransfer?.getData('text/plain');
  if (!data) return;
  try {
    const parsed = JSON.parse(data);
    if (parsed.typeID) {
      pendingCargo.value = { typeID: parsed.typeID, typeName: parsed.typeName || 'Item' };
      showQtyDialog.value = true;
    }
  } catch { /* ignore bad drag data */ }
}

function confirmCargoQty(quantity: number): void {
  if (pendingCargo.value) {
    store.addCargoToFit(pendingCargo.value.typeID, pendingCargo.value.typeName, quantity);
  }
  showQtyDialog.value = false;
  pendingCargo.value = null;
}

function cancelCargoQty(): void {
  showQtyDialog.value = false;
  pendingCargo.value = null;
}

function handleDragOver(e: DragEvent): void {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
}

function formatVol(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toFixed(0);
}
</script>

<template>
  <div class="cargo-panel" @drop="handleDrop" @dragover="handleDragOver">
    <!-- Stats bar -->
    <div class="cargo-stats-bar">
      <span class="cargo-stat" :class="{ over: capacityUsed > capacityTotal }">
        {{ formatVol(capacityUsed) }} / {{ formatVol(capacityTotal) }} m&sup3;
      </span>
    </div>

    <!-- Cargo list -->
    <div v-if="cargo.length > 0" class="cargo-list">
      <div
        v-for="item in cargo"
        :key="item.id"
        class="cargo-row"
      >
        <img :src="iconUrl(item.typeID, 32)" class="cargo-icon" :alt="item.typeName" />
        <div class="cargo-info">
          <span class="cargo-name">{{ item.typeName }}</span>
          <span class="cargo-meta">{{ (item.volume * item.quantity).toFixed(1) }} m&sup3;</span>
        </div>
        <span class="cargo-qty">x{{ item.quantity }}</span>
        <button class="cargo-remove" @click="removeCargo(item)" title="Remove item">&times;</button>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="cargo-empty">
      <span>Drag items from the Market browser</span>
    </div>

    <!-- Quantity dialog -->
    <CargoQtyDialog
      :visible="showQtyDialog"
      :type-name="pendingCargo?.typeName ?? ''"
      @confirm="confirmCargoQty"
      @cancel="cancelCargoQty"
    />
  </div>
</template>

<style scoped>
.cargo-panel {
  display: flex;
  flex-direction: column;
  min-height: 80px;
}

.cargo-stats-bar {
  display: flex;
  gap: 12px;
  padding: 4px 8px;
  font-size: 0.7rem;
  color: var(--text-secondary);
  border-bottom: 1px solid rgba(42, 63, 95, 0.3);
}

.cargo-stat.over {
  color: var(--accent-red);
  font-weight: 600;
}

.cargo-list {
  display: flex;
  flex-direction: column;
}

.cargo-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-bottom: 1px solid rgba(42, 63, 95, 0.2);
  transition: background var(--transition-fast);
}

.cargo-row:hover {
  background: var(--bg-hover);
}

.cargo-icon {
  width: 24px;
  height: 24px;
  border-radius: 2px;
  flex-shrink: 0;
}

.cargo-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.cargo-name {
  font-size: 0.78rem;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cargo-meta {
  font-size: 0.65rem;
  color: var(--text-secondary);
}

.cargo-qty {
  font-size: 0.72rem;
  color: var(--accent-blue);
  font-weight: 500;
  flex-shrink: 0;
}

.cargo-remove {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1rem;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0;
  transition: opacity var(--transition-fast), color var(--transition-fast);
}

.cargo-row:hover .cargo-remove {
  opacity: 1;
}

.cargo-remove:hover {
  color: var(--accent-red);
}

.cargo-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: var(--text-secondary);
  font-size: 0.75rem;
}
</style>
